import React, { useEffect, useRef, useState } from "react";
import { Formik, FormikProps } from "formik";
import { toast } from "react-toastify";
import Switch from "react-switch";
import SlideOutSidebar from "ui-components/slide-out-sidebar/slideOutSidebar";
import { Card } from "ui-components/card/card";
import ActionSection from "features/terminal-details/common/actionSection";
import XGSFormInput from "ui-components/form/input/xgsFormInput";
import XGSFormSelect from "ui-components/form/select/xgsFormSelect";
import { LabelModes } from "ui-components/molecules/labeled-inputs/labeledInput";
import ConfirmationModal from "ui-components/confirmation-modal/confirmationModal";
import { ReactComponent as AlertIcon } from "images/alert.svg";
import Loading from "ui-components/loading/loading";
import { usaStates } from "app/data/common/usaStates";
import {
  COVERAGE_STATUS_OPTIONS,
  EMPTY_SELECT_VALUE,
  EXTENSION_CODE_1_OPTIONS,
  EXTENSION_CODE_2_OPTIONS,
  ROUTE_GUIDE_FIELD_LABELS,
  SERVICE_DAY_AS400_OPTIONS,
  SERVICE_DAY_FULL_LABELS,
  SERVICE_DAY_KEYS,
  SERVICE_DAYS_REQUIRED_MESSAGE,
  THREE_PL_LANE_OPTIONS,
  ZipRoutingProfile,
  ZipRoutingProfileFormModel,
  emptyZipRoutingProfileForm,
  getZipRoutingProfileValidation,
  hasAtLeastOneServiceDay,
  mapProfileToForm,
  formatTerminalRichDisplay,
} from "app/data/zip-routing-profile/models";
import {
  useCreateZipRoutingProfile,
  useUpdateZipRoutingProfile,
  useZipRoutingProfileDetails,
} from "hooks/useZipRoutingProfiles";
import { useTerminalsList } from "hooks/useEquipment";
import "./routeGuideAddEdit.scss";

export interface RouteGuideAddEditProps {
  show: boolean;
  profileId?: string | null;
  initialProfile?: ZipRoutingProfile | null;
  onClose: () => void;
  onSaved?: () => void;
}

const RouteGuideAddEdit: React.FC<RouteGuideAddEditProps> = ({
  show,
  profileId,
  initialProfile,
  onClose,
  onSaved,
}) => {
  const formRef = useRef<FormikProps<ZipRoutingProfileFormModel>>(null);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const isEdit = !!profileId;

  const { data: terminalOptions = [], isLoading: terminalsLoading } = useTerminalsList();
  const {
    data: profileDetails,
    isLoading: detailsLoading,
    error: detailsError,
  } = useZipRoutingProfileDetails(show && isEdit ? profileId : null);

  const { mutate: createProfile, isLoading: isCreating } = useCreateZipRoutingProfile();
  const { mutate: updateProfile, isLoading: isUpdating } = useUpdateZipRoutingProfile();

  const terminalSelectOptions = terminalOptions.map((t) => ({
    label: formatTerminalRichDisplay(t),
    value: String(t.terminalNumber),
  }));

  const resolvedProfile = initialProfile || profileDetails || null;
  const initialValues: ZipRoutingProfileFormModel = resolvedProfile
    ? mapProfileToForm(resolvedProfile)
    : emptyZipRoutingProfileForm();

  useEffect(() => {
    if (!show) {
      formRef.current?.resetForm();
      setShowUnsavedChangesModal(false);
    }
  }, [show]);

  const handleCloseSidebar = () => {
    if (formRef.current?.dirty && !formRef.current?.isSubmitting) {
      setShowUnsavedChangesModal(true);
    } else {
      formRef.current?.resetForm();
      onClose();
    }
  };

  const handleSubmit = (values: ZipRoutingProfileFormModel) => {
    if (
      values.coverageStatus === "SERVICED" &&
      !hasAtLeastOneServiceDay(values)
    ) {
      toast.error(SERVICE_DAYS_REQUIRED_MESSAGE);
      return;
    }

    if (isEdit && profileId) {
      updateProfile(
        { id: profileId, payload: values },
        {
          onSuccess: () => {
            toast.success("Route guide profile updated.");
            formRef.current?.resetForm();
            onSaved?.();
            onClose();
          },
          onError: (error: any) => {
            toast.error(error?.message || "Unable to update route guide profile.");
          },
        }
      );
      return;
    }

    createProfile(values, {
      onSuccess: () => {
        toast.success("Route guide profile created.");
        formRef.current?.resetForm();
        onSaved?.();
        onClose();
      },
      onError: (error: any) => {
        toast.error(error?.message || "Unable to create route guide profile.");
      },
    });
  };
  const saving = isCreating || isUpdating;
  const loadingEdit = isEdit && detailsLoading && !initialProfile;

  return (
    <>
      <SlideOutSidebar
        header={
          isEdit
            ? `Edit ZIP ${resolvedProfile?.zipCode || resolvedProfile?.zip || ""}`
            : "Add Route Guide Profile"
        }
        show={show}
        onClose={handleCloseSidebar}
        size="small-605"
        className="xgs-route-guide-sidebar"
      >
        <div className="xgs-route-guide-add-edit">
          {loadingEdit && <Loading isLoading />}
          {detailsError && !initialProfile && (
            <div className="xgs-route-guide-add-edit__error">
              {(detailsError as Error)?.message || "Unable to load profile."}
            </div>
          )}
          {!loadingEdit && (
            <Formik
              innerRef={formRef}
              enableReinitialize
              initialValues={initialValues}
              validationSchema={getZipRoutingProfileValidation()}
              onSubmit={handleSubmit}
            >
              {(formik) => {
                const missingServiceDays =
                  formik.values.coverageStatus === "SERVICED" &&
                  !hasAtLeastOneServiceDay(formik.values);
                const missingTerminal =
                  formik.values.coverageStatus === "SERVICED" &&
                  formik.values.terminalNumber == null;
                const canSave =
                  formik.dirty &&
                  !saving &&
                  !missingServiceDays &&
                  !missingTerminal &&
                  /^\d{5}$/.test(formik.values.zipCode || "") &&
                  !!formik.values.state;

                const attemptSave = () => {
                  if (missingServiceDays) {
                    toast.error(SERVICE_DAYS_REQUIRED_MESSAGE);
                    return;
                  }
                  if (!canSave) return;
                  formik.submitForm();
                };

                return (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    attemptSave();
                  }}
                >
                  <Card title="Location" hideButtons>
                    <div className="xgs-route-guide-add-edit__grid">
                      <XGSFormInput
                        name="zipCode"
                        label="ZIP Code"
                        labelMode={LabelModes.column}
                        required
                        disabled={isEdit}
                        maxLength={5}
                        placeholder="5-digit ZIP"
                      />
                      <XGSFormSelect
                        name="state"
                        label="State"
                        labelMode={LabelModes.column}
                        required
                        options={usaStates}
                        isSearchable
                        formik
                        menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                        menuPosition="fixed"
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 10050 }),
                        }}
                        value={usaStates.find((s) => s.value === formik.values.state) || null}
                        onValueChange={(opt) => formik.setFieldValue("state", opt?.value || "")}
                      />
                    </div>
                  </Card>

                  <Card title="Servicing Terminal" hideButtons>
                    <div className="xgs-route-guide-add-edit__grid">
                      <XGSFormSelect
                        name="terminalNumber"
                        label="Terminal"
                        labelMode={LabelModes.column}
                        required={formik.values.coverageStatus === "SERVICED"}
                        options={terminalSelectOptions}
                        isSearchable
                        isLoading={terminalsLoading}
                        disabled={formik.values.coverageStatus === "NOT_SERVICED"}
                        formik
                        menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                        menuPosition="fixed"
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 10050 }),
                        }}
                        value={
                          formik.values.coverageStatus === "NOT_SERVICED"
                            ? null
                            : terminalSelectOptions.find(
                                (o) => o.value === String(formik.values.terminalNumber ?? "")
                              ) || null
                        }
                        onValueChange={(opt) =>
                          formik.setFieldValue(
                            "terminalNumber",
                            opt?.value != null ? Number(opt.value) : null
                          )
                        }
                      />
                      <XGSFormSelect
                        name="coverageStatus"
                        label="Coverage Status"
                        labelMode={LabelModes.column}
                        required
                        options={COVERAGE_STATUS_OPTIONS}
                        formik
                        menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                        menuPosition="fixed"
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 10050 }),
                        }}
                        value={
                          COVERAGE_STATUS_OPTIONS.find(
                            (o) => o.value === formik.values.coverageStatus
                          ) || null
                        }
                        onValueChange={(opt) => {
                          const nextStatus =
                            opt?.value === "NOT_SERVICED" ? "NOT_SERVICED" : "SERVICED";
                          formik.setFieldValue("coverageStatus", nextStatus);
                          if (nextStatus === "NOT_SERVICED") {
                            formik.setFieldValue("terminalNumber", null);
                          }
                        }}
                      />
                      <div className="xgs-route-guide-add-edit__switch-field">
                        <label>Service Type</label>
                        <div className="xgs-route-guide-add-edit__switch-row">
                          <Switch
                            checkedIcon={false}
                            uncheckedIcon={false}
                            width={32}
                            height={20}
                            onChange={(checked) => formik.setFieldValue("direct", checked)}
                            checked={!!formik.values.direct}
                          />
                          <span>{formik.values.direct ? "Direct" : "Indirect"}</span>
                        </div>
                      </div>
                    </div>
                  </Card>

                  <Card title="Routing codes" hideButtons>
                    <div className="xgs-route-guide-add-edit__grid">
                      {/* Quote switch hidden for now — quoteAllowed still defaults false in form/payload
                      <div className="xgs-route-guide-add-edit__switch-field">
                        <label>Quote allowed</label>
                        <div className="xgs-route-guide-add-edit__switch-row">
                          <Switch
                            checkedIcon={false}
                            uncheckedIcon={false}
                            width={32}
                            height={20}
                            onChange={(checked) => formik.setFieldValue("quoteAllowed", checked)}
                            checked={!!formik.values.quoteAllowed}
                          />
                          <span>{formik.values.quoteAllowed ? "Yes" : "No"}</span>
                        </div>
                      </div>
                      */}
                      <XGSFormSelect
                        name="threePlOriginCode"
                        label={ROUTE_GUIDE_FIELD_LABELS.threePlOriginCode}
                        labelMode={LabelModes.column}
                        options={THREE_PL_LANE_OPTIONS}
                        isSearchable={false}
                        formik
                        menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                        menuPosition="fixed"
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 10050 }),
                        }}
                        value={
                          THREE_PL_LANE_OPTIONS.find(
                            (o) => o.value === formik.values.threePlOriginCode
                          ) || THREE_PL_LANE_OPTIONS[0]
                        }
                        onValueChange={(opt) =>
                          formik.setFieldValue("threePlOriginCode", opt?.value ?? "")
                        }
                      />
                      <XGSFormSelect
                        name="threePlDestCode"
                        label={ROUTE_GUIDE_FIELD_LABELS.threePlDestCode}
                        labelMode={LabelModes.column}
                        options={THREE_PL_LANE_OPTIONS}
                        isSearchable={false}
                        formik
                        menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                        menuPosition="fixed"
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 10050 }),
                        }}
                        value={
                          THREE_PL_LANE_OPTIONS.find(
                            (o) => o.value === formik.values.threePlDestCode
                          ) || THREE_PL_LANE_OPTIONS[0]
                        }
                        onValueChange={(opt) =>
                          formik.setFieldValue("threePlDestCode", opt?.value ?? "")
                        }
                      />
                      <XGSFormSelect
                        name="extensionCode1"
                        label={ROUTE_GUIDE_FIELD_LABELS.extensionCode1}
                        labelMode={LabelModes.column}
                        options={EXTENSION_CODE_1_OPTIONS}
                        isSearchable={false}
                        formik
                        menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                        menuPosition="fixed"
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 10050 }),
                        }}
                        value={
                          EXTENSION_CODE_1_OPTIONS.find(
                            (o) => o.value === formik.values.extensionCode1
                          ) || EXTENSION_CODE_1_OPTIONS[0]
                        }
                        onValueChange={(opt) =>
                          formik.setFieldValue("extensionCode1", opt?.value ?? "")
                        }
                      />
                      <XGSFormSelect
                        name="extensionCode2"
                        label={ROUTE_GUIDE_FIELD_LABELS.extensionCode2}
                        labelMode={LabelModes.column}
                        options={EXTENSION_CODE_2_OPTIONS}
                        isSearchable={false}
                        formik
                        menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                        menuPosition="fixed"
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 10050 }),
                        }}
                        value={
                          EXTENSION_CODE_2_OPTIONS.find(
                            (o) => o.value === formik.values.extensionCode2
                          ) || EXTENSION_CODE_2_OPTIONS[0]
                        }
                        onValueChange={(opt) =>
                          formik.setFieldValue("extensionCode2", opt?.value ?? "")
                        }
                      />
                    </div>
                  </Card>

                  <Card title="Service Days" hideButtons>
                    <p className="xgs-route-guide-add-edit__hint">
                      AS400 values; meaning TBD.
                    </p>
                    <div className="xgs-route-guide-add-edit__days">
                      {SERVICE_DAY_KEYS.map((day) => (
                        <div key={day} className="xgs-route-guide-add-edit__day">
                          <XGSFormSelect
                            name={day}
                            label={SERVICE_DAY_FULL_LABELS[day]}
                            labelMode={LabelModes.column}
                            options={SERVICE_DAY_AS400_OPTIONS}
                            isSearchable={false}
                            formik
                            menuPortalTarget={
                              typeof document !== "undefined" ? document.body : undefined
                            }
                            menuPosition="fixed"
                            styles={{
                              menuPortal: (base) => ({ ...base, zIndex: 10050 }),
                            }}
                            value={
                              SERVICE_DAY_AS400_OPTIONS.find(
                                (o) => o.value === (formik.values[day] || EMPTY_SELECT_VALUE)
                              ) || SERVICE_DAY_AS400_OPTIONS[0]
                            }
                            onValueChange={(opt) =>
                              formik.setFieldValue(day, opt?.value ?? EMPTY_SELECT_VALUE)
                            }
                          />
                        </div>
                      ))}
                    </div>
                    {missingServiceDays && formik.dirty && (
                      <div className="xgs-route-guide-add-edit__error">
                        {SERVICE_DAYS_REQUIRED_MESSAGE}
                      </div>
                    )}
                  </Card>

                  <div className="xgs-route-guide-add-edit__actions">
                    <ActionSection
                      handleClear={handleCloseSidebar}
                      handleSave={attemptSave}
                      spinner={saving}
                      disabled={!canSave}
                      primaryButtonText={isEdit ? "Save Changes" : "Create Profile"}
                      secondaryButtonText="Cancel"
                    />
                  </div>
                </form>
                );
              }}
            </Formik>
          )}
        </div>
      </SlideOutSidebar>

      <ConfirmationModal
        opened={showUnsavedChangesModal}
        confirmButtonText="Discard"
        cancelButtonText="Keep Editing"
        onCancel={() => setShowUnsavedChangesModal(false)}
        onConfirm={() => {
          setShowUnsavedChangesModal(false);
          formRef.current?.resetForm();
          onClose();
        }}
      >
        <div className="xgs-confirmation-modal__icon">
          <AlertIcon />
        </div>
        You have unsaved changes. Discard them?
      </ConfirmationModal>
    </>
  );
};

export default RouteGuideAddEdit;
